import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Avatar, Box, Button, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { PlayArrowRounded, ReplayRounded } from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { accountSelector, setNaming } from '../../services/AccountSlice';
import { deleteAccount, fetchMyGames, signIn, signOut } from '../../account/session';
import { GoogleMark } from '../../account/AccountButton';
import { chipColor } from '../../theme';
import './mygames.css';

// A player's own games. Signed in: every browser on their account. As a
// guest: this browser's, with a nudge to sign in and keep them everywhere.

const LEVELS = [['easy', 'Easy'], ['medium', 'Medium'], ['hard', 'Hard']];
const MODE = { computer: 'vs Computer', challenge: 'Challenge', friends: 'Friends', online: 'Online' };
const RESULT = { won: 'Won', lost: 'Lost', draw: 'Draw' };

const clock = (seconds) => {
    if (seconds === null || seconds === undefined) return '–';
    const m = Math.floor(seconds / 60);
    return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}:${String(seconds % 60).padStart(2, '0')}`;
};
const when = (date) => new Date(date).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
const names = (people) => people.map(p => p.computer ? `${p.name} (computer)` : p.name).join(', ');

const GameRow = ({ game }) => (
    <li className={`mg-game ${game.result || 'playing'}`}>
        <span className="mg-chip" style={{ '--c': chipColor(game.team) || '#8A958F' }} aria-hidden="true" />
        <div className="mg-game-main">
            <div className="mg-game-top">
                <span className={`mg-result ${game.result || 'playing'}`}>{game.result ? RESULT[game.result] : 'In progress'}</span>
                <span className="mg-mode">{MODE[game.mode]}{game.difficulty ? ` · ${game.difficulty[0].toUpperCase()}${game.difficulty.slice(1)}` : ''}{game.board === 'shuffled' ? ' · Shuffled' : ''}</span>
            </div>
            <div className="mg-game-who">
                {game.opponents.length > 0 && <>against {names(game.opponents)}</>}
                {game.partners.length > 0 && <> · with {names(game.partners)}</>}
                {game.playedAs && <span className="mg-as"> · as {game.playedAs}</span>}
            </div>
        </div>
        <div className="mg-game-numbers">
            <span><strong>{game.myMoves}</strong> moves</span>
            <span>{clock(game.seconds)}</span>
            <span className="mg-when">{when(game.at)}</span>
        </div>
        {game.status === 'finished'
            ? <Button size="small" component={Link} to={`/r/${game.id}`} startIcon={<ReplayRounded />}>Replay</Button>
            : <Button size="small" variant="contained" component={Link} to={`/${game.id}`} startIcon={<PlayArrowRounded />}>Continue</Button>}
    </li>
);

const MyGames = () => {
    const dispatch = useDispatch();
    const { status, profile, busy } = useSelector(accountSelector);
    const [data, setData] = useState(null);
    const [failed, setFailed] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const load = useCallback(() => {
        setData(null);
        setFailed(null);
        fetchMyGames().then(setData).catch(error => setFailed(error.message || 'Could not load your games'));
    }, []);

    // Wait until we know who this is; load again when that changes.
    useEffect(() => {
        if (status !== 'unknown') {
            load();
        }
    }, [status, load]);

    const more = () => {
        setLoadingMore(true);
        fetchMyGames(data.more)
            .then(next => setData(current => ({ ...current, games: [...current.games, ...next.games], more: next.more })))
            .finally(() => setLoadingMore(false));
    };

    const signedIn = status === 'signedIn';
    const levels = data?.stats ? LEVELS.filter(([key]) => data.stats[key]) : [];

    return (
        <Box>
            <Helmet>
                <title>My Games | Play Sequence</title>
                <meta name="robots" content="noindex" />
            </Helmet>

            <Box className="mg-base-hero mg-hero">
                <Container maxWidth="md">
                    <div className="mg-who">
                        {signedIn && <Avatar src={profile.picture || undefined} imgProps={{ referrerPolicy: 'no-referrer' }} className="mg-avatar">{profile.displayName.charAt(0)}</Avatar>}
                        <div>
                            <Typography variant="h3" component="h1" sx={{ fontWeight: 800 }}>My games</Typography>
                            {signedIn && <Typography sx={{ color: 'rgba(243,238,226,0.75)' }}>
                                {profile.displayName}{profile.email ? ` · ${profile.email}` : ''}
                            </Typography>}
                            {status === 'guest' && <Typography sx={{ color: 'rgba(243,238,226,0.75)' }}>
                                Playing as a guest{data?.guestName ? <> — on the leaderboard as <strong>{data.guestName}</strong></> : null}
                            </Typography>}
                        </div>
                    </div>
                </Container>
            </Box>

            <Container maxWidth="md" sx={{ py: 4 }}>
                {status === 'guest' && <section className="mg-signin">
                    <div>
                        <h2>Keep your games everywhere</h2>
                        <p>These are the games played on this browser. Sign in with Google to keep them with you on any device, and to use your own name on the leaderboard.</p>
                    </div>
                    <Button variant="contained" onClick={() => !busy && signIn(dispatch)} startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <span className="mg-g"><GoogleMark /></span>}>{busy ? 'Signing in…' : 'Sign in with Google'}</Button>
                </section>}

                {(!data && !failed) && <div className="mg-base-loading"><CircularProgress size={28} /></div>}
                {failed && <p className="mg-base-empty">{failed}</p>}

                {levels.length > 0 && <section className="mg-stats" aria-label="Against the computer">
                    {levels.map(([key, label]) => {
                        const s = data.stats[key];
                        return (
                            <div className="mg-stat" key={key}>
                                <span className="mg-stat-level">vs Computer · {label}</span>
                                <strong>{s.wins}<small> / {s.games} won</small></strong>
                                <span>{s.winRate}% win rate</span>
                                {s.bestMoves !== null && <span>Best: {s.bestMoves} moves · fastest {clock(s.fastestSeconds)}</span>}
                                <Link to={`/leaderboard?level=${key}`}>Leaderboard</Link>
                            </div>
                        );
                    })}
                </section>}

                {data && <section className="mg-base-card">
                    <div className="mg-base-card-head"><h2>Games</h2></div>
                    {data.games.length === 0
                        ? <div className="mg-base-empty"><p>No games yet.</p><Button component={Link} to="/" variant="contained">Play a game</Button></div>
                        : <ul className="mg-games">{data.games.map(game => <GameRow key={game.id} game={game} />)}</ul>}
                    {data.more && <div className="mg-more">
                        <Button onClick={more} disabled={loadingMore}>{loadingMore ? 'Loading…' : 'Show older games'}</Button>
                    </div>}
                </section>}

                {signedIn && <section className="mg-account">
                    <h2>Account</h2>
                    <p>Signed in with Google{profile.browsers > 1 ? ` on ${profile.browsers} browsers` : ''}.</p>
                    <div className="mg-account-actions">
                        <Button variant="outlined" onClick={() => dispatch(setNaming(true))}>{profile.name ? 'Change leaderboard name' : 'Choose leaderboard name'}</Button>
                        <Button variant="outlined" onClick={() => signOut(dispatch)}>Sign out</Button>
                        <Button color="error" onClick={() => setConfirmDelete(true)}>Delete account</Button>
                    </div>
                </section>}
            </Container>

            <Dialog open={confirmDelete} onClose={() => !deleting && setConfirmDelete(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Delete your account?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        Your leaderboard name and your email are deleted, and every browser you signed in on goes back to playing as a guest.
                        Your results stay on the leaderboard under guest names. This can't be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setConfirmDelete(false)} disabled={deleting}>Keep my account</Button>
                    <Button color="error" variant="contained" disabled={deleting} onClick={async () => {
                        setDeleting(true);
                        try {
                            await deleteAccount(dispatch);
                            setConfirmDelete(false);
                        } finally {
                            setDeleting(false);
                        }
                    }}>{deleting ? 'Deleting…' : 'Delete account'}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MyGames;
